// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Hand-rolled SVG chart renderers. Every data mark carries a [data-tip] and an
// aria-label; interactive marks carry data-* for the view's click delegation.
import { escAttr } from '../format';
import { gridCells, histBars, polyline, scatterPoints } from '../utils/layout';
import { squarify } from '../utils/squarify';

export interface TreeItem { value: number; label: string; color: string; tip: string; id?: string }

/** Squarified treemap. Cells sized by value, labelled where they fit. */
export function svgTreemap(items: TreeItem[], width = 760, height = 320): string {
  const positive = items.filter((it) => it.value > 0);
  if (positive.length === 0) return `<svg class="chart" viewBox="0 0 ${width} ${height}"></svg>`;
  const rects = squarify(positive.map((it) => it.value), width, height);
  const parts: string[] = [`<svg class="chart chart-tree" viewBox="0 0 ${width} ${height}" role="img" aria-label="Treemap">`];
  rects.forEach((r, i) => {
    const it = positive[i];
    const idAttr = it.id !== undefined ? ` data-id="${escAttr(it.id)}"` : '';
    const cls = `tree-cell${it.id !== undefined ? ' clickable' : ''}`;
    parts.push(`<rect class="${cls}" x="${r.x.toFixed(2)}" y="${r.y.toFixed(2)}" width="${r.w.toFixed(2)}" height="${r.h.toFixed(2)}" fill="${it.color}"${idAttr} data-tip="${escAttr(it.tip)}" aria-label="${escAttr(it.tip)}"></rect>`);
    if (r.w > 46 && r.h > 22) {
      parts.push(`<text class="tree-label" x="${(r.x + 5).toFixed(2)}" y="${(r.y + 16).toFixed(2)}">${escAttr(it.label)}</text>`);
    }
  });
  parts.push('</svg>');
  return parts.join('');
}

export interface HistBin {
  label: string;
  count: number;
  color: string;
  tip: string;
  id?: string;
  active?: boolean;
}

/** Vertical histogram / bar column chart. `log` plots log10(count+1) heights. */
export function svgHistogram(
  bins: HistBin[],
  opts: { width?: number; height?: number; log?: boolean; maxOverride?: number } = {},
): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 240;
  const padB = 34;
  const padL = 4;
  const chartH = height - padB;
  const chartW = width - padL;
  const bars = histBars(bins.map((b) => b.count), chartW, chartH, 3, opts.maxOverride, opts.log);
  const showEveryLabel = bins.length <= 16;
  const parts: string[] = [];
  parts.push(`<svg class="chart chart-hist" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" aria-label="Bar chart">`);
  bars.forEach((bar, i) => {
    const b = bins[i];
    const x = padL + bar.x;
    const idAttr = b.id !== undefined ? ` data-id="${escAttr(b.id)}"` : '';
    const cls = `hist-bar${b.id !== undefined ? ' clickable' : ''}${b.active ? ' active' : ''}`;
    const barH = Math.max(b.count > 0 ? 1.5 : 0, bar.h);
    parts.push(
      `<rect class="${cls}" x="${x.toFixed(2)}" y="${(chartH - barH).toFixed(2)}" width="${bar.w.toFixed(2)}" height="${barH.toFixed(2)}" fill="${b.color}"${idAttr} data-tip="${escAttr(b.tip)}" aria-label="${escAttr(b.tip)}" tabindex="0" role="button"></rect>`,
    );
    if (showEveryLabel || i % Math.ceil(bins.length / 12) === 0) {
      parts.push(
        `<text class="axis-x" x="${(x + bar.w / 2).toFixed(2)}" y="${height - 18}" text-anchor="middle">${escAttr(b.label)}</text>`,
      );
    }
    if (bins.length <= 20 && b.count > 0) {
      parts.push(
        `<text class="bar-count" x="${(x + bar.w / 2).toFixed(2)}" y="${(chartH - barH - 4).toFixed(2)}" text-anchor="middle">${b.count}</text>`,
      );
    }
  });
  parts.push('</svg>');
  return parts.join('');
}

/** A cumulative log-count line over the same magnitude bins (Gutenberg–Richter). */
export function svgCumulativeLine(
  bins: HistBin[],
  cumulative: number[],
  opts: { width?: number; height?: number } = {},
): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 240;
  const padB = 34;
  const chartH = height - padB;
  const logs = cumulative.map((c) => (c > 0 ? Math.log10(c) : 0));
  const maxLog = Math.max(...logs, 0.0001);
  const pts = polyline(logs, [0, maxLog], width, chartH);
  const dots = logs
    .map((lv, i) => {
      const x = bins.length > 1 ? (i / (bins.length - 1)) * width : 0;
      const y = chartH - (lv / maxLog) * chartH;
      const tip = `${bins[i].label}: ${cumulative[i].toLocaleString()} events at or above`;
      return `<circle class="gr-dot" cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="3.5" data-tip="${escAttr(tip)}" aria-label="${escAttr(tip)}"></circle>`;
    })
    .join('');
  return `<svg class="chart chart-line" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" aria-label="Cumulative frequency line"><polyline class="gr-line" points="${pts}" fill="none"></polyline>${dots}</svg>`;
}

export interface ScatterPt { x: number; y: number; r: number; color: string; tip: string; id?: string }

/** Scatter plot with inverted y. Domains are data-space [min,max]. */
export function svgScatter(
  points: ScatterPt[],
  opts: { width?: number; height?: number; domX: [number, number]; domY: [number, number]; xLabel?: string; yLabel?: string; yTicks?: number[]; xTicks?: number[] },
): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 320;
  const padL = 44;
  const padB = 30;
  const plotW = width - padL - 8;
  const plotH = height - padB - 8;
  const mapped = scatterPoints(points.map((p) => ({ x: p.x, y: p.y })), opts.domX, opts.domY, plotW, plotH);
  const gridParts: string[] = [];
  (opts.yTicks ?? []).forEach((t) => {
    const yy = 8 + plotH - ((t - opts.domY[0]) / (opts.domY[1] - opts.domY[0] || 1)) * plotH;
    gridParts.push(`<line class="grid" x1="${padL}" y1="${yy.toFixed(1)}" x2="${width - 8}" y2="${yy.toFixed(1)}"></line>`);
    gridParts.push(`<text class="axis-y" x="${padL - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${t}</text>`);
  });
  (opts.xTicks ?? []).forEach((t) => {
    const xx = padL + ((t - opts.domX[0]) / (opts.domX[1] - opts.domX[0] || 1)) * plotW;
    gridParts.push(`<text class="axis-x" x="${xx.toFixed(1)}" y="${height - 14}" text-anchor="middle">${t}</text>`);
  });
  const dots = mapped
    .map((m, i) => {
      const p = points[i];
      const idAttr = p.id !== undefined ? ` data-id="${escAttr(p.id)}"` : '';
      return `<circle class="scatter-dot${p.id !== undefined ? ' clickable' : ''}" cx="${(padL + m.cx).toFixed(2)}" cy="${(8 + m.cy).toFixed(2)}" r="${p.r.toFixed(2)}" fill="${p.color}"${idAttr} data-tip="${escAttr(p.tip)}" aria-label="${escAttr(p.tip)}"></circle>`;
    })
    .join('');
  const xl = opts.xLabel ? `<text class="axis-label" x="${padL + plotW / 2}" y="${height - 1}" text-anchor="middle">${escAttr(opts.xLabel)}</text>` : '';
  const yl = opts.yLabel ? `<text class="axis-label" x="12" y="${8 + plotH / 2}" text-anchor="middle" transform="rotate(-90 12 ${8 + plotH / 2})">${escAttr(opts.yLabel)}</text>` : '';
  return `<svg class="chart chart-scatter" viewBox="0 0 ${width} ${height}" role="img" aria-label="Scatter plot">${gridParts.join('')}${dots}${xl}${yl}</svg>`;
}

export interface MatrixSpec {
  rowLabels: string[];
  colLabels: string[];
  cell: (r: number, c: number) => { value: number; color: string; tip: string };
  width?: number;
}

/** Row × column heatmap. Cells flush and non-overlapping (gridCells). */
export function svgMatrix(spec: MatrixSpec): string {
  const rows = spec.rowLabels.length;
  const cols = spec.colLabels.length;
  const labelW = 150;
  const width = spec.width ?? 760;
  const topH = 26;
  const gridW = width - labelW;
  const rowH = 30;
  const gridH = rows * rowH;
  const height = topH + gridH + 6;
  const cells = gridCells(rows, cols, gridW, gridH, 2);
  const parts: string[] = [];
  parts.push(`<svg class="chart chart-matrix" viewBox="0 0 ${width} ${height}" role="img" aria-label="Heatmap matrix">`);
  spec.colLabels.forEach((cl, c) => {
    const x = labelW + (c + 0.5) * (gridW / cols);
    parts.push(`<text class="axis-x" x="${x.toFixed(1)}" y="${topH - 8}" text-anchor="middle">${escAttr(cl)}</text>`);
  });
  spec.rowLabels.forEach((rl, r) => {
    const y = topH + (r + 0.5) * rowH + 4;
    parts.push(`<text class="matrix-rowlabel" x="${labelW - 8}" y="${y.toFixed(1)}" text-anchor="end">${escAttr(rl)}</text>`);
  });
  cells.forEach((cell) => {
    const d = spec.cell(cell.r, cell.c);
    parts.push(
      `<rect class="matrix-cell" x="${(labelW + cell.x).toFixed(2)}" y="${(topH + cell.y).toFixed(2)}" width="${cell.w.toFixed(2)}" height="${cell.h.toFixed(2)}" fill="${d.color}" data-tip="${escAttr(d.tip)}" aria-label="${escAttr(d.tip)}"></rect>`,
    );
    if (d.value > 0 && cell.w > 22) {
      parts.push(
        `<text class="matrix-val" x="${(labelW + cell.x + cell.w / 2).toFixed(2)}" y="${(topH + cell.y + cell.h / 2 + 4).toFixed(2)}" text-anchor="middle">${d.value}</text>`,
      );
    }
  });
  parts.push('</svg>');
  return parts.join('');
}

export interface DonutSeg { value: number; color: string; label: string; tip: string }

/** Donut chart. Segments with zero total render an empty ring. */
export function svgDonut(segs: DonutSeg[], size = 200): string {
  const total = segs.reduce((a, b) => a + b.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter * 0.62;
  if (total <= 0) {
    return `<svg class="chart chart-donut" viewBox="0 0 ${size} ${size}" role="img" aria-label="Donut chart"><circle cx="${cx}" cy="${cy}" r="${(rOuter + rInner) / 2}" fill="none" stroke="#1e293b" stroke-width="${rOuter - rInner}"></circle></svg>`;
  }
  let angle = -Math.PI / 2;
  const parts: string[] = [];
  for (const s of segs) {
    if (s.value <= 0) continue;
    const frac = s.value / total;
    const a2 = angle + frac * Math.PI * 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + rOuter * Math.cos(angle);
    const y1 = cy + rOuter * Math.sin(angle);
    const x2 = cx + rOuter * Math.cos(a2);
    const y2 = cy + rOuter * Math.sin(a2);
    const xi2 = cx + rInner * Math.cos(a2);
    const yi2 = cy + rInner * Math.sin(a2);
    const xi1 = cx + rInner * Math.cos(angle);
    const yi1 = cy + rInner * Math.sin(angle);
    const d = `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L ${xi2.toFixed(2)} ${yi2.toFixed(2)} A ${rInner} ${rInner} 0 ${large} 0 ${xi1.toFixed(2)} ${yi1.toFixed(2)} Z`;
    parts.push(`<path class="donut-seg" d="${d}" fill="${s.color}" data-tip="${escAttr(s.tip)}" aria-label="${escAttr(s.tip)}"></path>`);
    angle = a2;
  }
  return `<svg class="chart chart-donut" viewBox="0 0 ${size} ${size}" role="img" aria-label="Donut chart">${parts.join('')}</svg>`;
}

/** Simple line/area chart for a numeric series. */
export function svgLine(
  values: number[],
  opts: { width?: number; height?: number; domY?: [number, number]; color?: string; area?: boolean } = {},
): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 160;
  const max = opts.domY?.[1] ?? Math.max(...values, 1);
  const min = opts.domY?.[0] ?? 0;
  const pts = polyline(values, [min, max], width, height);
  const color = opts.color ?? '#38bdf8';
  const areaPath = opts.area && values.length > 1 ? `<polygon class="line-area" points="0,${height} ${pts} ${width},${height}" fill="${color}" opacity="0.14"></polygon>` : '';
  return `<svg class="chart chart-line" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" aria-label="Line chart">${areaPath}<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2"></polyline></svg>`;
}

export interface RankItem { label: string; frac: number; valueLabel: string; color: string; tip: string; id?: string; sub?: string }

/** Horizontal ranked bar rows (leaderboard). Responsive HTML, not SVG. */
export function rankRows(items: RankItem[]): string {
  return `<div class="rank-list">${items
    .map((it) => {
      const clickable = it.id !== undefined ? ` clickable" data-id="${escAttr(it.id)}" tabindex="0" role="button` : '';
      const pct = Math.max(0, Math.min(100, it.frac * 100));
      return `<div class="rank-row${clickable}" data-tip="${escAttr(it.tip)}">
        <div class="rank-label">${escAttr(it.label)}${it.sub ? `<span class="rank-sub">${escAttr(it.sub)}</span>` : ''}</div>
        <div class="rank-track"><div class="rank-fill" style="width:${pct.toFixed(1)}%;background:${it.color}"></div></div>
        <div class="rank-value">${escAttr(it.valueLabel)}</div>
      </div>`;
    })
    .join('')}</div>`;
}
